/**
 * Breeder Portal - Full Page Dashboard
 * ThePedigreeApp
 * 
 * Complete dashboard with Customers, Vendors, and all modules
 */

import { Link } from 'react-router-dom';
import { 
  Cat, Heart, Baby, ShoppingBag, Stethoscope, Receipt, Crown, Gift,
  FileText, DollarSign, Settings, MessageSquare, Dna, Plus, ArrowRight, 
  Activity, Users, Building2
} from 'lucide-react';
import { Button } from '../shared/UIComponents';
import { useAuth } from '../../hooks/useAuth';

const BreederDashboard = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* HERO */}
      <section className="bg-gradient-to-br from-pedigree-600 to-pedigree-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="text-4xl font-display font-bold mb-2">
                Welcome, {user?.displayName?.split(' ')[0] || 'Breeder'}! 🐱
              </h1>
              <p className="text-pedigree-100 text-lg">
                Manage your cattery, track litters, and grow your breeding business.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard/cats/new">
                <Button className="bg-white text-pedigree-600 hover:bg-pedigree-50">
                  <Plus className="w-4 h-4" /> Add New Cat
                </Button>
              </Link>
              <Link to="/dashboard/litters">
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  <Baby className="w-4 h-4" /> Register Litter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* CATS & BREEDING */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-gray-900">Cats & Breeding</h2>
              <p className="text-gray-500">Manage your cats, plan matings, and track litters</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/dashboard/cats" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-blue-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Cat className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">My Cats</h3>
                <p className="text-gray-500">View and manage all your breeding cats</p>
                <div className="mt-4 flex items-center text-blue-600 font-medium">
                  Manage Cats <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/breeding" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-pink-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Heart className="w-7 h-7 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Breeding Program</h3>
                <p className="text-gray-500">Plan matings and manage breeding pairs</p>
                <div className="mt-4 flex items-center text-pink-600 font-medium">
                  Plan Breeding <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/litters" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-purple-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Baby className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Litters & Kittens</h3>
                <p className="text-gray-500">Register litters and track kitten development</p>
                <div className="mt-4 flex items-center text-purple-600 font-medium">
                  Manage Litters <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/genetics" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-indigo-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Dna className="w-7 h-7 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Genetics Predictor</h3>
                <p className="text-gray-500">Predict colors, health risks & COI</p>
                <div className="mt-4 flex items-center text-indigo-600 font-medium">
                  Open Calculator <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* CUSTOMERS & SALES */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-gray-900">Customers & Sales</h2>
              <p className="text-gray-500">Manage buyers, track sales, and handle enquiries</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/dashboard/customers" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-green-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Users className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Customers</h3>
                <p className="text-gray-500">Manage buyers, sales records & contracts</p>
                <div className="mt-4 flex items-center text-green-600 font-medium">
                  View Customers <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/enquiries" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-yellow-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <MessageSquare className="w-7 h-7 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Enquiries</h3>
                <p className="text-gray-500">Respond to buyer messages and questions</p>
                <div className="mt-4 flex items-center text-yellow-600 font-medium">
                  View Messages <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/sales" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-teal-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <ShoppingBag className="w-7 h-7 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sales Overview</h3>
                <p className="text-gray-500">Sales summary and financial reports</p>
                <div className="mt-4 flex items-center text-teal-600 font-medium">
                  View Sales <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/registrations" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-cyan-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Registrations</h3>
                <p className="text-gray-500">Submit SACC, CASA & CFSA registrations</p>
                <div className="mt-4 flex items-center text-cyan-600 font-medium">
                  Submit Registration <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* HEALTH & RECORDS */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-gray-900">Health & Records</h2>
              <p className="text-gray-500">Track vaccinations, vet visits, and medical history</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/dashboard/health" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-red-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Stethoscope className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Health Records</h3>
                <p className="text-gray-500">Vaccinations, vet visits, medications, and test results</p>
                <div className="mt-4 flex items-center text-red-600 font-medium">
                  View Health Records <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/genetics" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-indigo-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Activity className="w-7 h-7 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Health Risk Analysis</h3>
                <p className="text-gray-500">Breed-specific genetic conditions and testing recommendations</p>
                <div className="mt-4 flex items-center text-indigo-600 font-medium">
                  Analyse Risks <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* FINANCES & BUSINESS */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Receipt className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-gray-900">Finances & Business</h2>
              <p className="text-gray-500">Track income, expenses, vendors, and assets</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/dashboard/finances" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-emerald-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Receipt className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Finances</h3>
                <p className="text-gray-500">Income, expenses & profit tracking</p>
                <div className="mt-4 flex items-center text-emerald-600 font-medium">
                  View Finances <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/vendors" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-blue-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Building2 className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Vendors & Suppliers</h3>
                <p className="text-gray-500">Vets, food suppliers, equipment</p>
                <div className="mt-4 flex items-center text-blue-600 font-medium">
                  Manage Vendors <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/assets" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-amber-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Crown className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fixed Assets</h3>
                <p className="text-gray-500">Cattery equipment & depreciation</p>
                <div className="mt-4 flex items-center text-amber-600 font-medium">
                  Manage Assets <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            <Link to="/dashboard/ledger" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-slate-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <DollarSign className="w-7 h-7 text-slate-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Platform Fees</h3>
                <p className="text-gray-500">R1,500 per litter fee & payments</p>
                <div className="mt-4 flex items-center text-slate-600 font-medium">
                  View Ledger <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* PARTNERS */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-gray-900">Partners & Affiliates</h2>
              <p className="text-gray-500">Earn from food partner referrals</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/dashboard/food-partners" className="group">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-orange-300 hover:shadow-xl transition-all h-full">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Gift className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Food Partners</h3>
                <p className="text-gray-500">Earn 8-10% from Royal Canin & Hills referrals</p>
                <div className="mt-4 flex items-center text-orange-600 font-medium">
                  View Partners <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* SETTINGS */}
        <section className="border-t border-gray-200 pt-12">
          <Link to="/dashboard/settings" className="group inline-flex items-center gap-4 bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-pedigree-300 hover:shadow-xl transition-all">
            <div className="w-14 h-14 bg-pedigree-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Settings className="w-7 h-7 text-pedigree-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Cattery Settings</h3>
              <p className="text-gray-500">Update your profile, cattery details, and preferences</p>
            </div>
            <ArrowRight className="w-6 h-6 text-gray-300 group-hover:text-pedigree-500 group-hover:translate-x-2 transition-all ml-4" />
          </Link>
        </section>

      </div>
    </div>
  );
};

export default BreederDashboard;
